import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { Chip, Grid, Typography } from '@mui/material';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import AlertBanner from 'lib/designSystem/AlertBanner';
import Card from 'lib/designSystem/Card';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SemiPieChart from 'lib/designSystem/SemiPieChart';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { SubProductId } from 'lib/types/models';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

export const STATUS_COLORS = {
  VALIDATING: 'warning',
  IN_REVIEW: 'info',
  ACTIVE: 'success',
  REJECTED: 'error',
} as const;

export const STATUS_LABELS = {
  VALIDATING: 'En validación',
  IN_REVIEW: 'En revisión',
  ACTIVE: 'Activa',
  REJECTED: 'Rechazada',
} as const;

export const STATUS_ICONS = {
  VALIDATING: AccessTimeIcon,
  IN_REVIEW: AssignmentIcon,
  ACTIVE: CheckCircleIcon,
  REJECTED: CancelIcon,
} as const;

export const SUBPRODUCT_LABELS: Record<SubProductId, string> = {
  'buy-order': 'Orden de compra',
  'check-invoice': 'Factura con flujo de cheques',
  contract: 'Contrato',
  'electronic-invoice': 'Factura Electrónica',
  'pending-account': 'Cuenta pendiente',
  'promissory-note': 'Pagaré',
  check: 'Cheque',
  'credit-request-cuota': 'Cuota de crédito',
  'offer-seriousness': 'Seriedad de oferta',
  'faithful-compliance': 'Fiel cumplimiento',
  'warranty-premium': 'Prima de garantía',
};

type SubProductConcentration = {
  subProductId: SubProductId;
  used: number;
  amount: number;
};

interface CreditLineData {
  status: 'VALIDATING' | 'IN_REVIEW' | 'ACTIVE' | 'REJECTED';
  amount: number;
  used: number;
  validUntil: string;
  subProducts: SubProductConcentration[];
}

const useCreditLine = ({ productId }: { productId: string }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [creditLineData, setCreditLineData] = useState<CreditLineData | null>(
    null,
  );

  const fetchCreditLine = async () => {
    try {
      if (id === undefined) return;
      setIsLoading(true);
      const response = await getCreditLineByProduct(dispatch, id, productId);
      setCreditLineData(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchCreditLine();
  }, [productId]);

  return { creditLineData, isLoading };
};

const formatPercentage = (used: number, total: number): string => {
  return `${Math.round((used / total) * 100)}%`;
};

const ProductMainInfo: React.FC<{ amount: number; used: number }> = ({
  amount,
  used,
}) => {
  const hasValidAmount = amount != null && amount !== 0;
  const hasValidUsed = used != null && used !== 0;
  const displayAmount = hasValidAmount ? formatMoney(amount) : 'Por definir';
  const displayPercentage =
    hasValidAmount && hasValidUsed
      ? formatPercentage(Number(used), Number(amount))
      : '-';

  return (
    <Grid
      container
      style={{
        justifyContent: 'space-between',
        padding: '0 4px',
        marginTop: 10,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        <span style={{ fontWeight: 600 }}>Monto Total:</span> {displayAmount}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <span style={{ fontWeight: 600 }}>Porcentaje Uso:</span>{' '}
        {displayPercentage}
      </Typography>
    </Grid>
  );
};

const NoCreditLineAlert: React.FC = () => {
  return (
    <AlertBanner
      severity="info"
      title="No existe una línea de crédito activa"
      message="El cliente no tiene una línea de crédito activa para este producto. Para acceder a los servicios de garantía, es necesario contar con una línea de crédito aprobada y activa."
      description="Puedes solicitar una línea de crédito para el cliente desde el panel de control de finanzas."
      customIcon={<InfoIcon />}
    />
  );
};

const CreditLineChart: React.FC<{ data: CreditLineData; status: string }> = ({
  data,
  status,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(280);
  const statusLabel = STATUS_LABELS[status as keyof typeof STATUS_LABELS];
  const StatusIcon = STATUS_ICONS[status as keyof typeof STATUS_ICONS];

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const totalData = [
    { name: 'Utilizado', value: data.used },
    { name: 'Disponible', value: data.amount - data.used },
  ];

  return (
    <div style={{ paddingTop: '18px' }}>
      <Grid container spacing={3} sx={{ marginBottom: 10 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <Grid container justifyContent="space-between" alignItems="center">
              <Typography
                variant="h4"
                style={{ textAlign: 'center', marginBottom: 2 }}
              >
                Monto Total
              </Typography>
              <Chip
                icon={
                  <StatusIcon
                    style={{
                      color: 'white',
                      fontSize: '16px',
                      marginLeft: '8px',
                    }}
                  />
                }
                label={statusLabel}
                color={STATUS_COLORS[status as keyof typeof STATUS_COLORS]}
              />
            </Grid>

            <ProductMainInfo amount={data.amount} used={data.used} />
            <div ref={containerRef}>
              <SemiPieChart
                data={totalData}
                money={true}
                width={containerWidth}
                height={200}
                cx={containerWidth / 2}
                cy={100}
              />
            </div>
          </Card>
          {data.validUntil && (
            <Typography
              variant="body1"
              color="text.secondary"
              gutterBottom
              style={{ marginTop: '10px' }}
            >
              Valida hasta:{' '}
              {formatDate(new Date(data.validUntil), 'DD/MM/YYYY')}
            </Typography>
          )}
        </Grid>

        {data.subProducts.map((product) => {
          const subProductData = [
            { name: 'Utilizado', value: product.used },
            { name: 'Disponible', value: product.amount - product.used },
          ];

          return (
            <Grid item xs={12} md={4} key={product.subProductId}>
              <Card>
                <Grid container justifyContent="space-between">
                  <Typography
                    variant="h4"
                    style={{ textAlign: 'center', marginBottom: 2 }}
                  >
                    {SUBPRODUCT_LABELS[product.subProductId]}
                  </Typography>
                </Grid>
                <ProductMainInfo amount={product.amount} used={product.used} />
                <div ref={containerRef}>
                  <SemiPieChart
                    data={subProductData}
                    money={true}
                    width={containerWidth}
                    height={200}
                    cx={containerWidth / 2}
                    cy={100}
                  />
                </div>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

// Main Component
const CreditLineDisplay: React.FC<{ productId: string }> = ({ productId }) => {
  const { creditLineData, isLoading } = useCreditLine({ productId });

  if (isLoading) return <CenteredCircularProgress />;

  if (!creditLineData?.status) return <NoCreditLineAlert />;

  const status = creditLineData.status;

  return <CreditLineChart data={creditLineData} status={status} />;
};

export default CreditLineDisplay;
